import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Snackbar,
  Alert
} from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { cloudCool, closeX as deleteIcon, floppyDisk as attachIcon } from '../../assets/images/icons';
import backend from '../utilities/Backend';
import DecorativeIcons from '../../components/DecorativeIcons';
import { cloud, floppyDisk, cassetteTape, sparkleGradientLarge } from '../../assets/images/icons';



const onboarding3Icons = [
  { src: cloud, alt: 'cloud', top: '6%', left: '4%', size: 44, opacity: 0.1, animation: 'float' },
  { src: floppyDisk, alt: 'floppy', top: '10%', right: '6%', size: 40, opacity: 0.12, animation: 'pulse' },
  { src: cassetteTape, alt: 'cassette', bottom: '12%', left: '7%', size: 46, opacity: 0.1, animation: 'float' },
  { src: sparkleGradientLarge, alt: 'sparkle', bottom: '18%', right: '5%', size: 38, opacity: 0.12, animation: 'float' },
];

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(4),
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider || 'rgba(167,139,250,0.2)'}`,
    borderRadius: '16px',
    boxShadow: '0 16px 40px rgba(0, 0, 0, 0.28)',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 2),
    },
  },
  backButton: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
  },
  uploadSection: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      boxShadow: `0 0 20px ${theme.palette.primary.main}4D, 0 0 40px ${theme.palette.secondary.main}26`,
    },
  },
  uploadIcon: {
    fontSize: 64,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  fileInput: {
    display: 'none',
  },
  fileList: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  nextButton: {
    marginTop: theme.spacing(4),
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(9, 9, 15, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
}));

const Onboarding3 = () => {
  const classes = useStyles();
  const history = useHistory();
  const fileInputRef = useRef(null);
  
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleBack = () => {
    history.push('/onboarding-2');
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        // Add metadata
        const metadata = {
          type: 'downloadable_content',
          purpose: 'onboarding'
        };
        formData.append('metadata', JSON.stringify(metadata));

        const response = await backend.post('v1/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          setUploadedFiles(prev => [...prev, {
            id: response.data.file_id,
            uuid: response.data.uuid,
            name: file.name,
            size: file.size,
            type: file.type,
            uploadedAt: new Date().toISOString()
          }]);
        } else {
          throw new Error(response.data.msg || 'Upload failed');
        }
      }
      
      setSuccess(`Successfully uploaded ${files.length} file(s)`);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload files');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleNext = () => {
    if (uploadedFiles.length === 0) {
      setError('Please upload at least one file to continue');
      return;
    }
    
    // Store uploaded files in sessionStorage to pass to next page
    sessionStorage.setItem('onboarding_files', JSON.stringify(uploadedFiles));
    history.push('/onboarding-3-1');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Box className={classes.root} style={{ position: 'relative' }}>
      <DecorativeIcons icons={onboarding3Icons} />
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper className={classes.paper} elevation={0}>
          {uploading && (
            <Box className={classes.loadingOverlay}>
              <CircularProgress />
            </Box>
          )}
          
          <IconButton className={classes.backButton} onClick={handleBack}>
            <ArrowBack />
          </IconButton>
          
          <Box style={{ paddingTop: 48 }}>
            <Typography variant="h3" gutterBottom color="primary">
              Upload Your Digital Content
            </Typography>
          </Box>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Upload the files you want to sell in your store
          </Typography>

          <Box
            className={classes.uploadSection}
            onClick={() => fileInputRef.current?.click()}
          >
            <img src={cloudCool} alt="" style={{ width: 64, height: 64, objectFit: 'contain' }} className={classes.uploadIcon} />
            <Typography variant="h6">
              Upload files here
            </Typography>
            <Typography variant="body2" color="textSecondary">
              All file types are supported
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<img src={attachIcon} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />}
              style={{ marginTop: 16 }}
            >
              Select Files
            </Button>
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className={classes.fileInput}
          />

          {uploadedFiles.length > 0 && (
            <Box className={classes.fileList}>
              <Typography variant="h6" gutterBottom>
                Uploaded Files ({uploadedFiles.length})
              </Typography>
              <List>
                {uploadedFiles.map((file) => (
                  <ListItem key={file.id} divider>
                    <ListItemText
                      primary={file.name}
                      secondary={formatFileSize(file.size)}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleRemoveFile(file.id)}
                        disabled={uploading}
                      >
                        <img src={deleteIcon} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            className={classes.nextButton}
            onClick={handleNext}
            disabled={uploading}
          >
            Next
          </Button>
        </Paper>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
        >
          <Alert onClose={() => setError('')} severity="error">
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!success}
          autoHideDuration={4000}
          onClose={() => setSuccess('')}
        >
          <Alert onClose={() => setSuccess('')} severity="success">
            {success}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Onboarding3;
